import { alphaSort, alphaSorter } from 'vet-bones/utils';

import {
  Mapping,
  MappingIdentifier,
  ModelConceptRelationship,
  RelationshipMapping,
} from 'src/containers/ProjectDiagram/types/data';
import { ModelSnapshot } from 'src/containers/ProjectDiagram/types/model';
import { isModelConceptRelationship } from 'src/containers/ProjectDiagram/utils/data';
import {
  isValidClassMapping,
  isValidRelationshipMapping,
} from 'src/containers/ProjectDiagram/utils/mapping/isValidMapping';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { RDFS_LABEL_IRI, XSD_STRING_IRI } from 'src/templates/queries/iris';
import {
  getFileUploadAttributeBinding,
  getFileUploadLabelBinding,
} from 'src/utils/stringify/mappings/bindings';
import {
  attributeDataSort,
  getMappingHelpers,
  mappingFieldSort,
  stringifyLabel,
} from 'src/utils/stringify/mappings/utils';

export const stringifyFileUploadMappings = ({
  classMappings,
  model,
  relationshipMappings,
  dataNamespace,
}: {
  model: ModelSnapshot;
  classMappings: Mapping[];
  relationshipMappings: RelationshipMapping[];
  dataNamespace: string;
}) => {
  const { attributesByIri, labelsByIri } = model.modelData;

  // triples are triples in the `TO` block, while bindings are statements in the
  // WHERE block. Triples are condensed using a semicolon for each subject, so only
  // the first triple for each unit array has three parts, while the rest have two
  const bindings: string[] = [];
  const triplesByIdentifierVariable: Record<string, string[]> = {};

  const { getBindingAndVariable, getStringifiedLabel, getUniqueVariable } =
    getMappingHelpers({ dataNamespace, labelsByIri });

  classMappings.forEach((mapping) => {
    if (!isValidClassMapping(mapping)) {
      return;
    }

    const classIri = getModelConceptClassValue(mapping.to as string);
    const { binding, identifierVariable } = getBindingAndVariable(
      classIri,
      mapping.primaryIdentifier
    );

    // binding is null if duplicate
    if (binding) {
      bindings.push(binding);
    }

    if (!triplesByIdentifierVariable[identifierVariable]) {
      triplesByIdentifierVariable[identifierVariable] = mapping.setClassType
        ? [`a <${classIri}>`]
        : [];
    }

    Object.values(mapping.fields).forEach((field) => {
      // we prepopulate the `usedVariables` record here
      // so that we avoid collisions with column names
      // that have similar names as our field variables
      getUniqueVariable(`?${field.value}`);
    });

    if (mapping.label) {
      const labelVariable = getUniqueVariable('?label');

      triplesByIdentifierVariable[identifierVariable].push(
        `<${RDFS_LABEL_IRI}> ${labelVariable}`
      );
      bindings.push(
        getFileUploadLabelBinding(XSD_STRING_IRI, mapping.label, labelVariable)
      );
    }

    Object.values(mapping.fields)
      .sort(mappingFieldSort)
      .forEach((field) => {
        if (!field.targets.length) {
          return;
        }

        field.targets.forEach((targetAttributeIri) => {
          const attribute = attributesByIri[targetAttributeIri];
          if (!attribute) {
            return;
          }

          const fieldLabel = stringifyLabel(field.label);

          Object.values(attribute.ranges)
            .sort(attributeDataSort)
            .forEach(({ value: rangeIri }) => {
              const rangeLabel = getStringifiedLabel(rangeIri);
              const fieldVariable = getUniqueVariable(
                `?${fieldLabel}_${rangeLabel}_field`
              );

              const triple = `<${targetAttributeIri}> ${fieldVariable}`;
              if (
                !triplesByIdentifierVariable[identifierVariable].includes(
                  triple
                )
              ) {
                triplesByIdentifierVariable[identifierVariable].push(
                  `<${targetAttributeIri}> ${fieldVariable}`
                );
                bindings.push(
                  getFileUploadAttributeBinding(
                    field.value,
                    rangeIri,
                    fieldVariable
                  )
                );
              }
            });
        });
      });
  });

  const relationshipsTriples = new Set<string>();
  relationshipMappings.forEach((relationshipMapping) => {
    const classMapping = classMappings.find(
      (mapping) =>
        mapping.key === (relationshipMapping.from || relationshipMapping.to)
    );
    if (
      !classMapping ||
      !isValidRelationshipMapping(relationshipMapping, classMapping)
    ) {
      return;
    }

    const relationshipData = model.linkDataArray.find(
      (linkData): linkData is ModelConceptRelationship =>
        Boolean(
          isModelConceptRelationship(linkData) &&
            linkData.value === relationshipMapping.relationshipIri &&
            linkData.from &&
            linkData.to
        )
    );

    if (!relationshipData) {
      return;
    }

    let fromClassIri: string | undefined;
    let fromPrimaryIdentifier: MappingIdentifier | undefined;
    if (relationshipMapping.from) {
      if (classMapping.to) {
        fromClassIri = getModelConceptClassValue(classMapping.to);
        fromPrimaryIdentifier = classMapping.primaryIdentifier;
      }
    } else if (
      relationshipMapping.fromClassIri &&
      relationshipMapping.fromPrimaryIdentifier
    ) {
      fromClassIri = relationshipMapping.fromClassIri;
      fromPrimaryIdentifier = relationshipMapping.fromPrimaryIdentifier;
    }

    let toClassIri: string | undefined;
    let toPrimaryIdentifier: MappingIdentifier | undefined;
    if (relationshipMapping.to) {
      if (classMapping.to) {
        toClassIri = getModelConceptClassValue(classMapping.to);
        toPrimaryIdentifier = classMapping.primaryIdentifier;
      }
    } else if (
      relationshipMapping.toClassIri &&
      relationshipMapping.toPrimaryIdentifier
    ) {
      toClassIri = relationshipMapping.toClassIri;
      toPrimaryIdentifier = relationshipMapping.toPrimaryIdentifier;
    }

    if (
      !fromClassIri ||
      !toClassIri ||
      !fromPrimaryIdentifier ||
      !toPrimaryIdentifier
    ) {
      return;
    }

    const { binding: fromClassBinding, identifierVariable: fromClassVariable } =
      getBindingAndVariable(fromClassIri, fromPrimaryIdentifier);
    const { binding: toClassBinding, identifierVariable: toClassVariable } =
      getBindingAndVariable(toClassIri, toPrimaryIdentifier);

    if (fromClassBinding) {
      bindings.push(fromClassBinding);
    }

    if (toClassBinding) {
      bindings.push(toClassBinding);
    }

    const fromClassVariableIsIri = fromClassVariable.startsWith('<');
    if (fromClassVariableIsIri) {
      const fromClassTriple = `${fromClassVariable} a <${fromClassIri}>`;
      relationshipsTriples.add(fromClassTriple);
    }

    const toClassVariableIsIri = toClassVariable.startsWith('<');
    if (toClassVariableIsIri) {
      const toClassTriple = `${toClassVariable} a <${toClassIri}>`;
      relationshipsTriples.add(toClassTriple);
    }

    const triple = `${fromClassVariable} <${relationshipData.value}> ${toClassVariable}`;

    relationshipsTriples.add(triple);
  });

  return `\
MAPPING
FROM CSV {
}
TO {
  ${Object.entries(triplesByIdentifierVariable)
    .filter(([, triples]) => triples.length)
    .sort(alphaSort(([identifierVariable]) => identifierVariable))
    .map(
      ([identifierVariable, triples]) =>
        `${identifierVariable} ${triples.join(' ;\n    ')} .`
    )
    .join('\n\n  ')}${
    relationshipsTriples.size
      ? `\n\n  ${Array.from(relationshipsTriples)
          .sort(alphaSorter)
          .join(' .\n\n  ')} .`
      : ''
  }
}
WHERE {
  ${bindings.join('\n  ')}
}`;
};
